import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import {colors, fonts} from "../../theme/tokens";

export type SegmentOption<T extends string> = {
    value: T;
    label: string;
    count?: number;
    dotColor?: string;
};

type SegmentPillsProps<T extends string> = {
    options: SegmentOption<T>[];
    value: T;
    onChange: (value: T) => void;
    "aria-label": string;
};

/** Single-select filter pills (28px, round) with a status dot and a count. */
export function SegmentPills<T extends string>({options, value, onChange, "aria-label": ariaLabel}: SegmentPillsProps<T>) {
    return (
            <Box role="group" aria-label={ariaLabel} sx={{display: "flex", flexWrap: "wrap", gap: 0.75}}>
                {options.map((option) => {
                    const selected = option.value === value;
                    return (
                            <ButtonBase
                                    key={option.value}
                                    onClick={() => onChange(option.value)}
                                    aria-pressed={selected}
                                    sx={{
                                        height: 28,
                                        px: 1.25,
                                        gap: 0.75,
                                        borderRadius: "14px",
                                        fontSize: "12.5px",
                                        border: `1px solid ${selected ? colors.primary : colors.cardBorder}`,
                                        backgroundColor: selected ? colors.infoTint : "#fff",
                                        color: selected ? colors.primary : colors.body,
                                        fontWeight: selected ? 500 : 400,
                                        "&:hover": {borderColor: selected ? colors.primary : colors.inputBorder},
                                        "&.Mui-focusVisible": {outline: `2px solid ${colors.primary}`, outlineOffset: 1},
                                    }}
                            >
                                {option.dotColor && (
                                        <Box aria-hidden sx={{width: 6, height: 6, borderRadius: "50%", backgroundColor: option.dotColor}} />
                                )}
                                <span>{option.label}</span>
                                {option.count !== undefined && (
                                        <Box component="span" sx={{fontFamily: fonts.mono, fontSize: "11px", color: selected ? colors.primary : colors.muted}}>
                                            {option.count}
                                        </Box>
                                )}
                            </ButtonBase>
                    );
                })}
            </Box>
    );
}
